import React from 'react'
import clsx from 'clsx'
import TableSortButton from './TableSortButton'
import TableHelpButton from './TableHelpButton'

export type TableThChildren =
  | React.ReactNode
  | ReturnType<typeof TableSortButton>
  | ReturnType<typeof TableHelpButton>

export type TableThProps = {
  /**
   * Defines the table header as sortable (ascending)
   * Default: `false`
   */
  sortable?: boolean

  /**
   * Defines the sortable column as the current active (ascending)
   * Default: `false`
   */
  active?: boolean

  /**
   * Defines the sortable column as in reversed order (descending)
   * Default: `false`
   */
  reversed?: boolean

  /**
   * If set to true, the header text will not wrap to new lines
   * Default: `false`
   */
  noWrap?: boolean

  /**
   * Defines the visual variant of the table header.
   * `emphasis` renders with medium font weight.
   * `subtle` renders with regular font weight, smaller font size, and lighter color.
   * Default: `emphasis`
   */
  variant?: 'emphasis' | 'subtle'

  /**
   * The content of the table header given as Tr.
   */
  children?: TableThChildren | Array<TableThChildren>
}

export default function Th(
  componentProps: TableThProps &
    React.ThHTMLAttributes<HTMLTableCellElement>
) {
  const {
    className,
    children,
    sortable,
    active,
    reversed,
    noWrap,
    variant,
    ...props
  } = componentProps

  const role =
    props.scope === 'row' || props.scope === 'rowgroup'
      ? 'rowheader'
      : 'columnheader'
  const scope = props.scope === 'row' ? 'row' : props.scope || 'col'
  const ariaSort = sortable
    ? reversed
      ? 'descending'
      : 'ascending'
    : undefined

  return (
    <th
      role={role}
      scope={scope}
      aria-sort={ariaSort}
      className={clsx(
        'dnb-table__th',
        sortable && 'dnb-table--sortable',
        active && 'dnb-table--active',
        reversed && 'dnb-table--reversed',
        noWrap && 'dnb-table--no-wrap',
        variant && `dnb-table__th--${variant}`,
        className
      )}
      {...props}
    >
      {children}
    </th>
  )
}

function Horizontal({
  className = null,
  ...rest
}: React.ThHTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={clsx('dnb-table__th__horizontal', className)}
    />
  )
}

Th.SortButton = TableSortButton
Th.HelpButton = TableHelpButton
Th.Horizontal = Horizontal
